import { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { AppContext } from '../../App.context';
import config from '../../config';
import { Article, ISSUE_LANG_KEY } from '../../constants';

import {
	useDownloadAssets,
	useArticle,
	useLoad,
	useTitle,
	useFont,
} from './Issue.hooks';
import { IssueContext } from './Issue.context';

import { ResponsiveView } from './responsive/Responsive';
import { ReplicaView } from './replica/Replica';

import { Loading } from './Loading';
import { useTrackingPageView } from '../../tracking/Tracking.hooks';

import { useFavorites } from '../../views/favorite/Favorite.hooks';
import { AssetsContext } from '../../assets/Assets.context';
import AudioPlayer from '../../components/player/audio/AudioPlayer';

export const IssueView = ({ onClickLogOut }: IssueViewProps) => {
	const location = useLocation();
	const { lang, sw } = useContext(AppContext);
	const { stylesMap } = useContext(AssetsContext);
	const [language, setLanguage] = useState<string>(getIssueLang(lang));
	const [mode, setMode] = useState<ContentType>(getDefaultMode());
	const { fontSize, setFontSize } = useFont();
	const { favorites, setFavorites } = useFavorites();
	const [audioPlayerUrl, setAudioPlayerUrl] = useState<string | null>(null);
	const [showAudioPlayer, setShowAudioPlayer] = useState(false);

	const { issue, isLoading, error, currentArticle, setCurrentArticle } =
		useLoad();
	const languages = useLanguages(issue);
	const articleLanguages = useArticleLanguages(currentArticle);
	const disabledLanguages = diffSet(languages, articleLanguages);

	useEffect(() => {
		localStorage.setItem('content-type', mode);
	}, [mode]);

	useDownloadAssets({ issue, sw });

	if (error) {
		console.error(error);
	}

	useArticle({ article: currentArticle, issue });

	useTitle({ article: currentArticle, lang });
	useTrackingPageView({
		title: currentArticle?.name[lang],
		location: location.pathname,
	});

	const articlesMap: Map<string, any> | undefined = useMemo(() => {
		if (!issue) {
			return undefined;
		}

		if (!issue.articles) {
			return undefined;
		}
		return issue.articles.reduce((acc: Map<string, any>, article: any) => {
			const { id } = article;
			acc.set(`${id}`, article);
			return acc;
		}, new Map<string, any>());
	}, [issue]);

	const internalArticlesMap: Map<number, Article> = useMemo(() => {
		if (!issue) {
			return new Map();
		}

		if (!issue.articles) {
			return new Map();
		}
		const { articles } = issue;
		return articles.reduce(
			(acc: Map<number, Article>, article: Article) => {
				const { ArticleID } = article;
				acc.set(parseInt(`${ArticleID}`, 10), article);
				return acc;
			},
			new Map(),
		);
	}, [issue]);

	const navigateToArticle = useCallback(
		(articleId: number) => {
			const selectedArticle = internalArticlesMap.get(articleId);
			if (!selectedArticle) {
				return;
			}
			setCurrentArticle(selectedArticle);
		},
		[internalArticlesMap],
	);

	if (
		isLoading ||
		!currentArticle ||
		!stylesMap ||
		!stylesMap.size ||
		issue === null
	) {
		return <Loading />;
	}

	return (
		<IssueContext.Provider
			value={{
				language,
				issue,
				currentArticle,
				setCurrentArticle,
				articles: issue?.articles ? issue.articles : [],
				sw,
				setMode,
				articlesMap,
				fontSize,
				setFontSize,
				favorites,
				setFavorites,
				navigateToArticle,
				languages,
				articleLanguages,
				disabledLanguages,
				setLanguage,
				onClickLogOut,
				audioPlayerUrl,
				setAudioPlayerUrl,
				showAudioPlayer,
				setShowAudioPlayer,
			}}
		>
			<HeadHelmet />
			<AudioPlayer
				url={audioPlayerUrl}
				onClose={() => {
					setAudioPlayerUrl(null);
				}}
			>
				{mode === 'responsive' ? <ResponsiveView /> : <ReplicaView />}
			</AudioPlayer>
		</IssueContext.Provider>
	);
};

interface IssueViewProps {
	onClickLogOut?: (url?: string) => void;
}

function getIssueLang(lang: string): string {
	if (!localStorage.getItem(ISSUE_LANG_KEY)) {
		localStorage.setItem(ISSUE_LANG_KEY, lang);
	}

	return `${localStorage.getItem(ISSUE_LANG_KEY)}`;
}

function HeadHelmet() {
	const { currentArticle, language } = useContext(IssueContext);
	const { role, name, thumbnail } = currentArticle;
	const keywords =
		currentArticle?.keywords && currentArticle.keywords.length
			? currentArticle.keywords
			: [];
	const keywordsString = [...new Set(keywords)].join(',');
	return (
		<Helmet>
			<meta property="og:type" content={role} />
			{name?.[language] ? (
				<meta property="og:title" content={name?.[language]} />
			) : null}
			<meta property="og:url" content={window.location.href} />
			{thumbnail ? (
				<meta property="og:image" content={thumbnail} />
			) : null}
			<meta property="og:category" content="digital-editions" />
			{keywordsString.length ? (
				<meta name="keywords" content={keywordsString}></meta>
			) : null}
			{keywordsString.length ? (
				<meta name="post-tags" content={keywordsString}></meta>
			) : null}
		</Helmet>
	);
}

function getDefaultMode(): ContentType {
	const { contentType } = config.issue;
	switch (contentType) {
		case 'replica':
		case 'responsive':
			return contentType;
		default:
			if (!localStorage.getItem('content-type')) {
				localStorage.setItem('content-type', 'responsive');
			}
			return localStorage.getItem('content-type') as ContentType;
	}
}

type ContentType = 'replica' | 'responsive' | 'responsive-replica';

function useLanguages(issue: any): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!issue) {
			return;
		}

		if (!issue.articles || !issue.articles.length) {
			return;
		}

		const acc = new Set<string>();
		for (const article of issue.articles) {
			for (const lang in article.name) {
				acc.add(lang);
			}
		}
		setLanguages(acc);
	}, [issue]);

	return languages;
}

function useArticleLanguages(article: any): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!article) {
			return;
		}

		const acc = new Set<string>();
		for (const lang in article.name) {
			acc.add(lang);
		}

		setLanguages(acc);
	}, [article]);
	return languages;
}

function diffSet(a: Set<any>, b: Set<any>): Set<any> {
	const diff = [...a].filter((i) => !b.has(i));
	return new Set([...diff]);
}

export default IssueView;
